/**
 * useTendrilStream — low-level NDJSON stream consumer for the Tendril
 * streaming transport (tendril-streaming-responses Phase 3, WI-007).
 *
 * MIRRORING NOTE (binding): the frame interfaces and the tolerant line
 * decoder below are a structural mirror of
 * `backend/src/lib/tendril/ndjson-protocol.ts` (the wire-protocol source of
 * truth). The frontend (Vite/browser bundle) cannot import backend TS source
 * across the build boundary, so this file re-declares the same shapes rather
 * than importing them. If the backend protocol changes, this file must be
 * updated to match — there is no type-level enforcement of that today.
 *
 * Scope carried over from the backend header comment: the underlying model
 * call is non-streaming, so `TokenFrame` carries the FULL buffered answer as
 * ONE frame, not per-token chunks. Consumers must not fabricate incremental
 * chunking on top of this.
 *
 * Stall detection (AC-ERROR-3 / JD-1): the wire has no `meta.budget` field
 * today (see `MetaFrame` below), so this module always falls back to the
 * hardcoded `STALL_TIMEOUT_MS` default (20000ms, pinned by the creative
 * doc). The timer resets on EVERY chunk read from the underlying reader
 * (not only on fully-parsed lines) — this also covers `ping` frames, which
 * arrive as part of a chunk and are never surfaced to a frame handler.
 */

// ---------------------------------------------------------------------------
// Frame types (mirror of backend/src/lib/tendril/ndjson-protocol.ts)
// ---------------------------------------------------------------------------

interface BaseFrame {
  seq: number;
}

export interface MetaFrame extends BaseFrame {
  kind: 'meta';
  protocolVersion: string;
  turnId: string;
  conversationId: string;
}

export interface StatusFrame extends BaseFrame {
  kind: 'status';
  text: string;
}

export interface ToolFrame extends BaseFrame {
  kind: 'tool';
  capability: string;
  statusClass: string;
  latencyMs: number;
}

/** Carries the FULL assistant answer as a single frame — see file header. */
export interface TokenFrame extends BaseFrame {
  kind: 'token';
  text: string;
}

export interface PendingActionFrame extends BaseFrame {
  kind: 'pending_action';
  pendingAction: {
    capabilityName: string;
    args: Record<string, unknown>;
    confirmTemplate: string;
    userId?: string;
  };
}

export interface UiActionsFrame extends BaseFrame {
  kind: 'ui_actions';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  actions: any[];
}

export interface DegradedFrame extends BaseFrame {
  kind: 'degraded';
  cause: 'soft_deadline' | 'hard_ceiling';
}

export interface DoneFrame extends BaseFrame {
  kind: 'done';
  outcome: 'ok' | 'degraded' | 'error' | 'aborted';
}

export interface ErrorFrame extends BaseFrame {
  kind: 'error';
  message: string;
  code?: string;
}

export interface PingFrame extends BaseFrame {
  kind: 'ping';
}

export interface ProtocolErrorFrame extends BaseFrame {
  kind: 'protocol_error';
  reason: string;
}

export type TendrilNdjsonFrame =
  | MetaFrame
  | StatusFrame
  | ToolFrame
  | TokenFrame
  | PendingActionFrame
  | UiActionsFrame
  | DegradedFrame
  | DoneFrame
  | ErrorFrame
  | PingFrame
  | ProtocolErrorFrame;

// ---------------------------------------------------------------------------
// Stall + copy constants (pinned by the creative doc — binding, no exceptions)
// ---------------------------------------------------------------------------

/**
 * No `meta.budget` field exists on the wire today (see `MetaFrame` above) —
 * this hardcoded default is the ONLY source for the stall threshold. If a
 * budget field is ever added to `MetaFrame`, this module should derive the
 * timeout from it instead; documented here rather than silently guessed.
 */
export const STALL_TIMEOUT_MS = 20_000;

export const STALL_MESSAGE =
  "Connection interrupted. Tendril's response stopped partway through.";

// ---------------------------------------------------------------------------
// Tolerant line decoder (mirror of createNdjsonDecoder)
// ---------------------------------------------------------------------------

const MAX_RESIDUAL_BYTES = 1024 * 1024; // 1 MiB, mirrors the backend cap.

function createLocalNdjsonDecoder(
  onFrame: (frame: unknown) => void,
  onProtocolError: (reason: string) => void,
) {
  const textDecoder = new TextDecoder('utf-8', { fatal: false });
  let residual = '';

  const consumeLine = (line: string): void => {
    if (line.length === 0) return;
    try {
      onFrame(JSON.parse(line));
    } catch {
      onProtocolError('Malformed JSON line');
    }
  };

  return {
    push(chunk: Uint8Array | ArrayBuffer): void {
      const bytes = chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk);
      residual += textDecoder.decode(bytes, { stream: true });

      let newlineIdx = residual.indexOf('\n');
      while (newlineIdx !== -1) {
        const line = residual.slice(0, newlineIdx);
        residual = residual.slice(newlineIdx + 1);
        consumeLine(line);
        newlineIdx = residual.indexOf('\n');
      }

      if (residual.length > MAX_RESIDUAL_BYTES) {
        onProtocolError('Unterminated line exceeded 1 MiB residual cap');
        residual = '';
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Stream consumer
// ---------------------------------------------------------------------------

export interface TendrilStreamHandlers {
  onMeta?: (frame: MetaFrame) => void;
  onStatus?: (frame: StatusFrame) => void;
  onTool?: (frame: ToolFrame) => void;
  onToken?: (frame: TokenFrame) => void;
  onPendingAction?: (frame: PendingActionFrame) => void;
  onUiActions?: (frame: UiActionsFrame) => void;
  onDegraded?: (frame: DegradedFrame) => void;
  onDone?: (frame: DoneFrame) => void;
  /** Server-sent `error` frame only — never called for the client-synthesized stall. */
  onError?: (frame: ErrorFrame) => void;
  onProtocolError?: (reason: string) => void;
  /** Client-synthesized stall timeout — distinct from a server `error` frame. */
  onStall?: (message: string) => void;
}

export interface ConsumeStreamOptions {
  /** Defaults to STALL_TIMEOUT_MS. Injectable for tests. */
  stallMs?: number;
  /** Injectable timer functions so tests can use Vitest fake timers deterministically. */
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
}

/**
 * True when the response looks like it actually started an NDJSON stream
 * (2xx + `application/x-ndjson` content-type). False means "never started
 * streaming" per the backend contract (auth/claim failures return a plain
 * JSON error response BEFORE any stream bytes) — callers use this to decide
 * whether an automatic per-turn fallback to the buffered endpoint is safe.
 */
export function isNdjsonStreamResponse(response: Response): boolean {
  if (!response.ok) return false;
  const contentType = response.headers.get('content-type') ?? '';
  return contentType.includes('application/x-ndjson');
}

/**
 * Reads `response.body`, decodes NDJSON lines, and dispatches typed
 * callbacks per frame kind. Owns the stall timer (reset on every chunk read
 * from the underlying reader, per the file header). Never throws — a stall,
 * a protocol error, or a `done`/`error` frame all resolve the returned
 * promise normally; the caller inspects which handler fired to determine
 * the outcome.
 */
export async function consumeTendrilStream(
  response: Response,
  handlers: TendrilStreamHandlers,
  options: ConsumeStreamOptions = {},
): Promise<void> {
  const stallMs = options.stallMs ?? STALL_TIMEOUT_MS;
  const setTimeoutFn = options.setTimeoutFn ?? setTimeout;
  const clearTimeoutFn = options.clearTimeoutFn ?? clearTimeout;

  if (!response.body) {
    handlers.onProtocolError?.('Response has no readable body');
    return;
  }

  const reader = response.body.getReader();
  let terminal = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let stallTimer: any = null;

  const clearStallTimer = (): void => {
    if (stallTimer !== null) {
      clearTimeoutFn(stallTimer);
      stallTimer = null;
    }
  };

  const armStallTimer = (): void => {
    clearStallTimer();
    stallTimer = setTimeoutFn(() => {
      if (terminal) return;
      terminal = true;
      handlers.onStall?.(STALL_MESSAGE);
      reader.cancel().catch(() => {
        // Reader may already be closed — nothing more to do.
      });
    }, stallMs);
  };

  const handleFrame = (frame: unknown): void => {
    if (
      frame === null ||
      typeof frame !== 'object' ||
      typeof (frame as { kind?: unknown }).kind !== 'string'
    ) {
      handlers.onProtocolError?.('Frame missing kind');
      return;
    }
    const typed = frame as TendrilNdjsonFrame;
    switch (typed.kind) {
      case 'ping':
        // Never surfaced — chunk arrival already reset the stall timer.
        return;
      case 'meta':
        handlers.onMeta?.(typed);
        return;
      case 'status':
        handlers.onStatus?.(typed);
        return;
      case 'tool':
        handlers.onTool?.(typed);
        return;
      case 'token':
        handlers.onToken?.(typed);
        return;
      case 'pending_action':
        handlers.onPendingAction?.(typed);
        return;
      case 'ui_actions':
        handlers.onUiActions?.(typed);
        return;
      case 'degraded':
        handlers.onDegraded?.(typed);
        return;
      case 'done':
        terminal = true;
        clearStallTimer();
        handlers.onDone?.(typed);
        return;
      case 'error':
        terminal = true;
        clearStallTimer();
        handlers.onError?.(typed);
        return;
      case 'protocol_error':
        handlers.onProtocolError?.(typed.reason);
        return;
      default:
        // Forward-compat: ignore frame kinds this client doesn't know about.
        return;
    }
  };

  const decoder = createLocalNdjsonDecoder(handleFrame, (reason) => {
    handlers.onProtocolError?.(reason);
  });

  armStallTimer();
  try {
    while (!terminal) {
      const { value, done } = await reader.read();
      if (done) break;
      // Reset on EVERY chunk, per the file header — not only on parsed lines.
      armStallTimer();
      if (value) decoder.push(value);
    }
  } finally {
    clearStallTimer();
  }
}

/**
 * Thin hook wrapper (naming convention for `frontend/src/hooks/`). The
 * consumer itself holds no React state — `consumeTendrilStream` and
 * `isNdjsonStreamResponse` are plain, directly-importable functions so they
 * can be unit-tested and driven imperatively from `useTendrilChat.js`
 * without going through a render cycle.
 */
export function useTendrilStream(): {
  consumeTendrilStream: typeof consumeTendrilStream;
  isNdjsonStreamResponse: typeof isNdjsonStreamResponse;
} {
  return { consumeTendrilStream, isNdjsonStreamResponse };
}
